import { Injectable } from "@angular/core";
import {
  InvoiceDetail,
  InvoiceHeader,
  ItemDetail,
  ItemDetailStatusEnum,
  JoDetail,
  JoHeader,
  ShipBin,
  ShipFrequency,
} from "app/model/entities/entity-model";
import { InvoiceStatusCode } from "app/model/enums/invoice-status-code";
import { ShipFrequencyCode } from "app/model/enums/ship-frequency-code";
import { ByWeekday, RRule } from "rrule";
import _ from "lodash";
import { DateFns } from "./date-fns";
import { DbQueryService } from "./db-query.service";
import { DbSaveService } from "./db-save.service";
import { UnitOfWork } from "./unit-of-work";
import { UtilFns } from "./util-fns";

@Injectable({ providedIn: "root" })
export class ShipBinService {
  constructor(
    private uow: UnitOfWork,
    private dbQueryService: DbQueryService,
    private dbSaveService: DbSaveService,
  ) {}

  /** Find an existing open ShipBin for this JoHeader's shipping key, or create a new one. */
  async findOrCreateShipBin(joh: JoHeader) {
    const shipBinGroupingKey = UtilFns.stringHash64(joh.shippingKey);
    let shipBin = await this.dbQueryService.getShipBinByKey(shipBinGroupingKey);
    const expectedShipDate = this.calcExpectedShipDate(joh.shipFrequency);
    if (shipBin == null) {
      shipBin = this.uow.createEntity(ShipBin, {
        aggregateGroupingKey: shipBinGroupingKey,
        shipFrequencyId: joh.shipFrequencyId,
        isOpen: true,
        expectedShipDate: expectedShipDate,
        priorityOrder: 8888,
      });
    }
    return shipBin;
  }

  /** Calculate the next expected ship date based on ship frequency rules. */
  calcExpectedShipDate(shipFrequency: ShipFrequency) {
    const today = DateFns.startOfDay(new Date());

    let interval = 0;
    switch (shipFrequency.id) {
      case ShipFrequencyCode.Immediate:
        return today;
      case ShipFrequencyCode.WhenBinIsFull:
        return today;
      case ShipFrequencyCode.EveryWeek:
        interval = 1;
        break;
      case ShipFrequencyCode.Every2Weeks:
        interval = 2;
        break;
      case ShipFrequencyCode.Every3Weeks:
        interval = 3;
        break;
      default:
        break;
    }

    const startDate = DateFns.startOfYear(today);
    const minBinDate = DateFns.dateAdd(today, shipFrequency.minDaysForNewBin ?? 1, "days");
    const rrule = new RRule({
      freq: RRule.WEEKLY,
      interval: interval,
      byweekday: <ByWeekday>shipFrequency.rRuleByWeekDay ?? "MO",
      dtstart: startDate,
    });
    return rrule.after(minBinDate);
  }

  /** Create a voucher (InvoiceHeader + InvoiceDetails) for items in a ship bin belonging to a single JO.
   *  Used during bin release when the Ops Director decides to ship a bin.
   *  @param joh - The JoHeader these items belong to
   *  @param invItds - The item details from ONE bin for ONE JO
   *  @param shipBin - The existing ship bin (already assigned to items)
   */
  async initializeInvoice(joh: JoHeader, invItds: ItemDetail[], shipBin: ShipBin) {
    const billableItds = invItds.filter((itd) => itd.isShippableGoods);
    if (billableItds.length === 0) {
      throw new Error("The system cannot make a voucher. None of these items can ship.");
    }

    const invoiceHeaderId = await this.dbQueryService.getNextInvoiceHeaderId(joh.id);
    const invh = {} as InvoiceHeader;
    const now = new Date();
    invh.id = invoiceHeaderId;

    invh.invoiceDate = DateFns.startOfDay(now);
    const appConfig = this.dbQueryService.appConfig;
    const dueDays = joh.account.dueDateNumDays || appConfig.account.dueDateNumDays;
    invh.dueDate = DateFns.dateAdd(invh.invoiceDate, dueDays, "days");

    invh.crtnTs = now;
    invh.shipTs = now;

    invh.invoiceStatusId = InvoiceStatusCode.Voucher;
    invh.joHeaderId = joh.id;
    invh.shipBin = shipBin;

    if (!joh.invoiceHeaders.length) {
      invh.prepaymentAmt = joh.prepaymentAmt;
    } else {
      const prevTotal = joh.getInvoiceTotal();
      invh.prepaymentAmt = prevTotal < 0 ? -prevTotal : 0;
    }

    billableItds.forEach((itd) => {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessVoucher;
      itd.invoiceHeaderId = invoiceHeaderId;
    });

    const invoiceHeader = this.uow.createEntity(InvoiceHeader, invh);
    const joDetails = _.uniq(billableItds.map((itd) => itd.joDetail));
    joDetails.forEach((jod) => {
      const invd = {} as InvoiceDetail;
      invd.invoiceHeaderId = invoiceHeader.id;
      invd.joDetailId = jod.id;

      const relatedItds = billableItds.filter((x) => jod.id == x.joDetailId);
      invd.redistributeQty = relatedItds.filter((itd) => itd.accountId !== null).length;
      invd.shipQty = relatedItds.length - invd.redistributeQty;

      this.uow.createEntity(InvoiceDetail, invd);
    });

    invoiceHeader.subtotalAmt = (invoiceHeader.calcExtendedAmt() ?? 0) + (invoiceHeader.calcExtendedReissueAmt() ?? 0);
    const isTaxExempt = !!invoiceHeader.joHeader.account?.accountTaxExemptionNumber?.trim();
    invoiceHeader.taxAmt = isTaxExempt ? 0 : invoiceHeader.subtotalAmt * (invoiceHeader.joHeader.taxRate || 0);
    invoiceHeader.totalAmt =
      invoiceHeader.subtotalAmt + invoiceHeader.taxAmt + (invoiceHeader.orderHandlingAmt ?? 0) - (invoiceHeader.prepaymentAmt ?? 0);

    return invoiceHeader;
  }
}
